import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ReactMarkdown from 'react-markdown';

import type { EndsMysteryShopperCall } from '@ballerine/common';

import { Download, FileText, PhoneCall } from '../../../../../common/icons/lucide-adapter';
import { markdownComponents } from '../../../report-shared/components/markdown-components';
import { ReportWrapper } from '../../../report-shared/components/ReportWrapper';
import { CallPill } from './CallPill';
import { CallRecordingPlayer } from './CallRecordingPlayer';
import { isAbsoluteHttpUrl } from './utils';

dayjs.extend(utc);

type MysteryShopperCallSectionProps = {
  mysteryShopperCall?: EndsMysteryShopperCall | null;
};

const DetailLabel = ({ children }: { children: ReactNode }) => (
  <div className="text-sm text-slate-400">{children}</div>
);

// The authored `timezoneLabel` names the zone the call happened in, so the
// time must be rendered at the ISO string's own offset — converting to the
// viewer's local timezone would show a different wall-clock time still
// labelled with the authored zone.
const OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/;

const toAuthoredOffset = (value: string) => {
  const parsed = dayjs(value);
  const offset = OFFSET_RE.exec(value)?.[1];

  if (!parsed.isValid() || !offset) {
    return parsed;
  }

  if (offset === 'Z') {
    return parsed.utcOffset(0);
  }

  const sign = offset.startsWith('-') ? -1 : 1;
  const digits = offset.slice(1).replace(':', '');

  return parsed.utcOffset(sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2))));
};

export const MysteryShopperCallSection = ({
  mysteryShopperCall,
}: MysteryShopperCallSectionProps) => {
  if (!mysteryShopperCall) {
    return null;
  }

  const {
    dateTime,
    timezoneLabel,
    phoneNumber,
    language,
    callStatus,
    callOutcome,
    recording,
    transcript,
    summary,
  } = mysteryShopperCall;

  const parsedDate = dateTime ? toAuthoredOffset(dateTime) : null;
  const formattedDateTime =
    parsedDate?.isValid() === true
      ? `${parsedDate.format('MMM D, YYYY - h:mm A')}${timezoneLabel ? ` (${timezoneLabel})` : ''}`
      : null;

  const recordingUrl =
    recording?.url != null && isAbsoluteHttpUrl(recording.url) ? recording.url : null;
  const transcriptUrl =
    transcript?.url != null && isAbsoluteHttpUrl(transcript.url) ? transcript.url : null;
  const hasArtifacts = recordingUrl != null || transcriptUrl != null;

  return (
    <ReportWrapper
      title={
        <span className="inline-flex items-center gap-2">
          <PhoneCall className="h-6 w-6" aria-hidden="true" />
          Mystery Shopper Call
        </span>
      }
      showTooltip={false}
    >
      <div className="flex flex-wrap gap-x-14 gap-y-4">
        {formattedDateTime && (
          <div>
            <DetailLabel>Date &amp; Time</DetailLabel>
            <div className="mt-1 text-sm font-medium text-slate-900">{formattedDateTime}</div>
          </div>
        )}
        {phoneNumber && (
          <div>
            <DetailLabel>Phone Number</DetailLabel>
            <div className="mt-1 text-sm font-medium text-slate-900">{phoneNumber}</div>
          </div>
        )}
        {language && (
          <div>
            <DetailLabel>Language</DetailLabel>
            <div className="mt-1 text-sm font-medium text-slate-900">{language}</div>
          </div>
        )}
        {callStatus && (
          <div>
            <DetailLabel>Call Status</DetailLabel>
            <div className="mt-1">
              <CallPill value={callStatus} />
            </div>
          </div>
        )}
        <div>
          <DetailLabel>Call Outcome</DetailLabel>
          <div className="mt-1">
            <CallPill value={callOutcome} />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-900">
          Voice Recording
        </h4>
        {hasArtifacts ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {recordingUrl && (
              <CallRecordingPlayer
                url={recordingUrl}
                durationSeconds={recording?.durationSeconds}
              />
            )}
            {transcriptUrl && (
              <div className="flex items-center gap-4 rounded-2xl border border-gray-200 px-5 py-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  <FileText className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">Full Transcript</div>
                  <div className="text-sm text-slate-500">PDF</div>
                </div>
                <a
                  href={transcriptUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download full transcript as PDF"
                  className="shrink-0 text-slate-400 transition-colors hover:text-slate-600"
                >
                  <Download className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-700">
            No recording or transcript available for this call.
          </p>
        )}
      </div>

      {summary && (
        <div className="mt-8">
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-900">
            Summary
          </h4>
          <div className="rounded-2xl bg-gray-50 px-6 py-5">
            <ReactMarkdown components={markdownComponents}>{summary}</ReactMarkdown>
          </div>
        </div>
      )}
    </ReportWrapper>
  );
};
